import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkles, ArrowLeft, Play } from "lucide-react";
import Logo from "../components/Logo";
import PhoneMockup from "../components/PhoneMockup";

const faqs = [
  {
    title: "What does it do?",
    body: "My Mood Groove combines AI conversation with curated soundscapes to help users move into the emotional state they want — whether that is calm, focus, sleep, reflection, creativity, or renewal.",
  },
];

/** Lightweight intersection-observer based reveal-on-scroll. */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    el.querySelectorAll(".reveal").forEach((node) => io.observe(node));
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ─────────────────────────────────────────────────────────────
   HeroIntroSequence — three cinematic hero images crossfade,
   then the atmospheric video takes over and loops forever.
   ───────────────────────────────────────────────────────────── */
const HERO_IMAGES = [
  "/pitch/slide-standing-portal.png",  // cosmic scale
  "/pitch/slide-meditation-orb.png",   // personal moment
  "/pitch/slide-heart-wearable.png",   // embodied practice
];
const HERO_IMAGE_DURATION = 3200; // ms per image
const HERO_CROSSFADE = 900;       // ms crossfade

function HeroIntroSequence() {
  // stage: 0, 1, 2 = image i; 3 = video
  // On subsequent visits within the same session, skip straight to the video.
  const [stage, setStage] = useState(() => {
    if (typeof window === "undefined") return 0;
    return sessionStorage.getItem("mmg_investor_intro_seen") === "1" ? 3 : 0;
  });
  useEffect(() => {
    HERO_IMAGES.forEach((src) => { const im = new Image(); im.src = src; });
  }, []);
  useEffect(() => {
    if (stage >= 3) {
      // Mark the intro as seen so re-navigating to /investor skips it.
      try { sessionStorage.setItem("mmg_investor_intro_seen", "1"); } catch (_) {}
      return;
    }
    const t = setTimeout(() => setStage((s) => s + 1), HERO_IMAGE_DURATION);
    return () => clearTimeout(t);
  }, [stage]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {HERO_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          className={`hero-intro-img absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out ${stage === i ? "opacity-100 hero-intro-active" : "opacity-0"}`}
          style={{ transitionDuration: `${HERO_CROSSFADE}ms` }}
        />
      ))}
      <video
        className={`absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out ${stage >= 3 ? "opacity-100 hero-intro-active" : "opacity-0"}`}
        style={{ transitionDuration: `${HERO_CROSSFADE}ms` }}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        data-testid="investor-hero-video"
      >
        <source src="/investor/atmospheric-loop.webm" type="video/webm" />
        <source src="/investor/atmospheric-loop.mp4" type="video/mp4" />
      </video>
      {/* Readability overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(3,6,15,0.45) 0%, rgba(3,6,15,0.18) 45%, rgba(3,6,15,0.05) 75%, rgba(3,6,15,0.20) 100%)",
        }}
      />
      {/* Soft bottom fade into next section */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#030407]" />
      {/* Intro progress dots — fade out once video begins */}
      <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 transition-opacity duration-700 ${stage >= 3 ? "opacity-0" : "opacity-80"}`}>
        {HERO_IMAGES.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all duration-500 ${i === stage ? "w-6 bg-amber-300" : "w-1 bg-white/45"}`}
          />
        ))}
      </div>
    </div>
  );
}


export default function Preview() {
  const containerRef = useReveal();
  const navigate = useNavigate();

  if (sessionStorage.getItem("mmg_investor_access") !== "granted") {
    return <Navigate to="/investor-access" replace />;
  }

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen w-full overflow-x-hidden"
      data-testid="preview-page"
    >
      {/* Top sticky bar */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#030407]/60 border-b border-white/5"
        data-testid="preview-header"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem("mmg_investor_access"); sessionStorage.removeItem("mmg_investor_intro_seen"); window.location.assign("/"); }}
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white transition text-sm"
            data-testid="investor-exit-portal"
          >
            <ArrowLeft size={16} /> Exit Portal
          </button>
          <Logo size="md" />
          <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-zinc-400">
            <Sparkles size={12} className="text-amber-300" />
            <span>Private Investor Preview</span>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-14 pb-6 sm:pt-20 sm:pb-8 px-6 sm:px-10 overflow-hidden">
        {/* Intro sequence — 3 hero images crossfade, then atmospheric video takes over */}
        <HeroIntroSequence />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="reveal is-visible max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[11px] uppercase tracking-[0.28em] text-zinc-300 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              Investor Preview · Confidential
            </div>
            <h1 className="hero-title font-heading font-medium text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02] text-balance">
              My Mood <span className="font-medium">Groove</span>
            </h1>
            <p className="hero-copy mt-6 max-w-2xl font-body text-lg sm:text-xl text-zinc-100/95 leading-relaxed text-balance">
              An AI-guided emotional awareness and soundscape platform that
              helps people shift, reflect, focus, calm down, create, and
              reconnect with themselves through personalized conversation,
              immersive audio, and mood based experiences.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/investor/deck")}
                className="btn-premium inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm tracking-tight shadow-[0_10px_30px_rgba(238,216,154,0.28)]"
                data-testid="hero-cta-pitch-deck"
              >
                <Play size={14} />
                View pitch deck
              </button>
              <a
                href="/walkthrough.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-white font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition"
                data-testid="hero-cta-walkthrough"
              >
                Experience the First Session
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS MY MOOD GROOVE */}
      <section
        className="relative pt-4 pb-6 sm:pt-6 sm:pb-8 px-6 sm:px-10"
        data-testid="what-section"
      >
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute top-10 left-1/3 w-[40vw] h-[40vw] rounded-full bg-violet-500/10 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-5 sm:gap-6 max-w-3xl">
            {faqs.map((f, i) => (
              <article
                key={f.title}
                className="reveal is-visible group glass rounded-2xl p-7 sm:p-8 transition hover:-translate-y-1 hover:bg-white/[0.06] hover:border-white/20"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`faq-card-${i + 1}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-heading text-xs text-amber-300 tracking-[0.2em]">
                    0{i + 1}
                  </span>
                  <h3 className="font-heading text-xl sm:text-2xl text-white font-medium tracking-tight">
                    {f.title}
                  </h3>
                </div>
                <p className="font-body text-zinc-300/90 leading-relaxed text-[15px]">
                  {f.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VISION BAND — first illustration, no overlapping text */}
      <section
        className="relative w-full pt-4 pb-12 sm:pt-6 sm:pb-16 px-6 sm:px-10"
        data-testid="vision-band-section"
      >
        <div className="max-w-7xl mx-auto">
          <img
            src="/preview-bg.png"
            alt=""
            aria-hidden="true"
            className="block w-full h-auto max-h-[80vh] object-contain mx-auto select-none pointer-events-none"
            style={{ opacity: 0.85 }}
          />
        </div>
      </section>

      {/* APP PREVIEW — reference imagery + single CTA into the walkthrough */}
      <section
        id="app-preview"
        className="relative py-24 sm:py-32 px-6 sm:px-10"
        data-testid="mood-sound-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-[40vw] h-[40vw] rounded-full bg-violet-500/10 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[35vw] h-[35vw] rounded-full bg-pink-500/10 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="mb-12 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">
              The experience
            </div>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              From pressure to awareness — one screen at a time.
            </h2>
            <p className="mt-5 font-body text-zinc-300/90 leading-relaxed text-balance max-w-2xl">
              A single believable journey through a first session in My Mood
              Groove — arrival, atmosphere, conversation, perspective.
              Voice-guided, ambient-scored, and fully interactive.
            </p>
          </div>

          {/* Reference imagery — hero display */}
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black/30">
            <img
              src="/opening-screens.jpg"
              alt="A row of five My Mood Groove screens: welcome, emotional check-in, define your intent, midnight focus chat, and create your groove."
              className="w-full h-auto block select-none"
              draggable={false}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(3,4,7,0) 55%, rgba(3,4,7,0.85) 100%)",
              }}
            />
          </div>

          {/* Single CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-body text-zinc-400 max-w-md leading-relaxed text-sm">
              Best experienced with sound. The walkthrough opens in its own
              full-bleed environment with ambient audio at 432 Hz — headphones
              recommended.
            </p>
            <a
              href="/walkthrough.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-premium shrink-0 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white text-black font-heading font-medium text-sm tracking-tight"
              data-testid="app-preview-cta"
            >
              Experience the First Session
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* INVESTOR MATERIALS */}
      <section
        id="investor-materials"
        className="relative py-24 sm:py-32 px-6 sm:px-10"
        data-testid="investor-materials-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] rounded-full bg-violet-500/10 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="reveal glass-strong rounded-3xl p-10 sm:p-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-4">
              Investor materials
            </div>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              The full <span className="italic font-medium">investor deck.</span>
            </h2>
            <p className="mt-4 font-body text-zinc-300/90 max-w-xl mx-auto leading-relaxed">
              Vision, product architecture, market positioning, expansion
              pathways, and the path to a multi-modal mood platform.
            </p>

            <div className="mt-10 flex items-center justify-center">
              <button
                type="button"
                onClick={() => navigate("/investor/deck")}
                className="btn-premium inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm tracking-tight shadow-[0_10px_30px_rgba(238,216,154,0.28)]"
                data-testid="view-pitch-deck-button"
              >
                <Play size={16} />
                View pitch deck
              </button>
            </div>
            <p className="mt-5 text-[11px] text-zinc-500 font-body tracking-wide">
              Confidential — 14 slides · approx. 6 minutes · best with sound
            </p>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 text-zinc-500 text-xs font-body">
            <div className="flex items-center gap-2">
              <Logo size="md" />
            </div>
            <div className="flex items-center gap-4">
              <span>© {new Date().getFullYear()} My Mood Groove</span>
              <span className="hidden sm:inline">·</span>
              <span className="text-amber-200/60" data-testid="patent-pending-note">U.S. Patent Pending · Confirmed August 2026</span>
              <span className="hidden sm:inline">·</span>
              <button
                type="button"
                className="hover:text-zinc-300 transition"
                data-testid="footer-exit-portal"
                onClick={() => { sessionStorage.removeItem("mmg_investor_access"); sessionStorage.removeItem("mmg_investor_intro_seen"); window.location.assign("/"); }}
              >
                Exit Portal
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
